import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../../Icon/Icon';
import Modal from '../../Modal/Modal';
import ModalHeader from '../../Modal/ModalHeader/ModalHeader';
import ModalBody from '../../Modal/ModalBody/ModalBody';
import ModalFooter from '../../Modal/ModalFooter/ModalFooter';
import CommonButton from '../../Buttons/CommonButton';
import { formatNumber } from '../../../utils/functions';
import styles from './ExpectedStatsSyncModal.module.css';

const METRICS = [
    {
        key: 'impressions',
        labelKey: 'common.words.impressions',
        step: '1',
    },
    {
        key: 'impacts',
        labelKey: 'common.words.impacts',
        step: '1',
    },
    {
        key: 'spent',
        labelKey: 'common.words.spent',
        step: '0.01',
    },
];

const normalizeMetricValue = (value) => {
    if (value === null || value === undefined || value === '') {
        return null;
    }

    const parsedValue = Number(value);
    return Number.isFinite(parsedValue) && parsedValue >= 0
        ? parsedValue
        : null;
};

const normalizeExpectedTargets = (expectedTargets = {}) => {
    if (!expectedTargets || typeof expectedTargets !== 'object') {
        return {};
    }

    return Object.entries(expectedTargets).reduce(
        (accumulator, [screenId, metrics]) => {
            const normalizedScreenId = String(screenId || '').trim();
            if (!normalizedScreenId) {
                return accumulator;
            }

            accumulator[normalizedScreenId] = {
                impressions: normalizeMetricValue(metrics?.impressions),
                impacts: normalizeMetricValue(metrics?.impacts),
                spent: normalizeMetricValue(metrics?.spent),
            };

            return accumulator;
        },
        {}
    );
};

const normalizeScreenStatsRow = (statsRow = {}) => {
    return {
        screenId: String(
            statsRow?.screenId ||
                statsRow?.screen_id ||
                statsRow?.screenid ||
                statsRow?.id ||
                ''
        ).trim(),
        screenName: String(
            statsRow?.screenName ||
                statsRow?.screen_name ||
                statsRow?.screen ||
                statsRow?.name ||
                ''
        ).trim(),
        currentTotals: {
            impressions: normalizeMetricValue(statsRow?.impressions),
            impacts: normalizeMetricValue(statsRow?.impacts),
            spent: normalizeMetricValue(statsRow?.spent),
        },
    };
};

const buildScreenRows = (campaign, syncPreview, editableTargets, screenStats) => {
    const configuredTargets = normalizeExpectedTargets(
        campaign?.expected_stats_targets
    );
    const verification = Array.isArray(syncPreview?.verification)
        ? syncPreview.verification
        : [];
    const missingTargetScreens = syncPreview?.missingTargetScreens || [];
    const screens = Array.isArray(campaign?.screen)
        ? campaign.screen
        : Array.isArray(campaign?.screens)
          ? campaign.screens
          : [];
    const normalizedScreenStats = Array.isArray(screenStats)
        ? screenStats.map(normalizeScreenStatsRow)
        : [];
    const rowMap = new Map();
    const statsById = new Map();
    const statsByName = new Map();

    normalizedScreenStats.forEach((statsRow) => {
        if (statsRow.screenId) {
            statsById.set(statsRow.screenId, statsRow);
        }
        if (statsRow.screenName) {
            statsByName.set(statsRow.screenName.toLowerCase(), statsRow);
        }
    });

    screens.forEach((screen) => {
        const isScreenObject = screen && typeof screen === 'object';
        const rawScreenId = isScreenObject ? screen?.id : screen;
        const screenId = String(rawScreenId || '').trim();
        if (!screenId) {
            return;
        }

        const screenName = isScreenObject
            ? screen?.name || screen?.alias || screenId
            : screenId;
        const matchedStats =
            statsById.get(screenId) ||
            statsByName.get(String(screenName).toLowerCase()) ||
            null;

        rowMap.set(screenId, {
            screenId,
            screenName: matchedStats?.screenName || screenName,
            rows: 0,
            hasStatsMatch: Boolean(matchedStats),
            currentTotals: matchedStats?.currentTotals || {},
            targetTotals: editableTargets[screenId] || configuredTargets[screenId] || {},
        });
    });

    Object.keys(configuredTargets).forEach((screenId) => {
        if (!rowMap.has(screenId)) {
            const matchedStats =
                statsById.get(screenId) ||
                normalizedScreenStats.find((statsRow) => {
                    return (
                        statsRow.screenName &&
                        statsRow.screenName.toLowerCase() ===
                            screenId.toLowerCase()
                    );
                }) ||
                null;

            rowMap.set(screenId, {
                screenId,
                screenName: matchedStats?.screenName || screenId,
                rows: 0,
                hasStatsMatch: Boolean(matchedStats),
                currentTotals: matchedStats?.currentTotals || {},
                targetTotals:
                    editableTargets[screenId] || configuredTargets[screenId] || {},
            });
        }
    });

    verification.forEach((screenVerification) => {
        const screenId = String(screenVerification?.screenId || '').trim();
        if (!screenId) {
            return;
        }

        const baseRow = rowMap.get(screenId) || {
            screenId,
            screenName: screenId,
        };

        rowMap.set(screenId, {
            ...baseRow,
            rows: screenVerification?.rows || 0,
            hasStatsMatch: true,
            currentTotals: screenVerification?.currentTotals || {},
            targetTotals:
                editableTargets[screenId] ||
                normalizeExpectedTargets({
                    [screenId]: screenVerification?.targetTotals || {},
                })[screenId] ||
                configuredTargets[screenId] ||
                {},
        });
    });

    missingTargetScreens.forEach((screenId) => {
        const normalizedScreenId = String(screenId || '').trim();
        if (!normalizedScreenId || rowMap.has(normalizedScreenId)) {
            return;
        }

        rowMap.set(normalizedScreenId, {
            screenId: normalizedScreenId,
            screenName: normalizedScreenId,
            rows: 0,
            hasStatsMatch: false,
            currentTotals: {},
            targetTotals:
                editableTargets[normalizedScreenId] ||
                configuredTargets[normalizedScreenId] ||
                {},
        });
    });

    return Array.from(rowMap.values()).map((screenRow) => ({
        ...screenRow,
        targetTotals:
            editableTargets[screenRow.screenId] ||
            screenRow.targetTotals ||
            {},
    }));
};

const formatMetricValue = (metricKey, value, currencySymbol) => {
    if (value === null || value === undefined || Number.isNaN(Number(value))) {
        return '-';
    }

    const parsedValue = Number(value);

    if (metricKey === 'spent') {
        return `${currencySymbol || ''}${formatNumber(parsedValue, 'currency')}`;
    }

    return formatNumber(parsedValue, 'rounded');
};

const formatDeltaValue = (
    metricKey,
    currentValue,
    targetValue,
    currencySymbol
) => {
    if (
        currentValue === null ||
        currentValue === undefined ||
        targetValue === null ||
        targetValue === undefined ||
        Number.isNaN(Number(currentValue)) ||
        Number.isNaN(Number(targetValue))
    ) {
        return '-';
    }

    const delta = Number(targetValue) - Number(currentValue);
    const prefix = delta > 0 ? '+' : delta < 0 ? '-' : '';

    return `${prefix}${formatMetricValue(
        metricKey,
        Math.abs(delta),
        currencySymbol
    )}`;
};

const getDeltaTone = (currentValue, targetValue) => {
    if (
        currentValue === null ||
        currentValue === undefined ||
        targetValue === null ||
        targetValue === undefined ||
        Number.isNaN(Number(currentValue)) ||
        Number.isNaN(Number(targetValue))
    ) {
        return 'text-gray-400';
    }

    const delta = Number(targetValue) - Number(currentValue);

    if (delta > 0) {
        return 'text-emerald-600';
    }

    if (delta < 0) {
        return 'text-amber-600';
    }

    return 'text-gray-500';
};

const hasAnyConfiguredValue = (targetTotals = {}) => {
    return METRICS.some((metric) => {
        const metricValue = targetTotals?.[metric.key];
        return (
            metricValue !== null &&
            metricValue !== undefined &&
            metricValue !== '' &&
            !Number.isNaN(Number(metricValue))
        );
    });
};

const ExpectedStatsSyncModal = ({
    active,
    toggler,
    onSave,
    onConfirmSync,
    isSaving = false,
    isApplying = false,
    campaign,
    syncPreview,
    screenStats = [],
}) => {
    const { t } = useTranslation();
    const verification = syncPreview?.verification || [];
    const missingTargetScreens = syncPreview?.missingTargetScreens || [];
    const changedRows = syncPreview?.changedRows || 0;
    const currencySymbol = campaign?.currency?.symbol || '';
    const hasPreview = Boolean(syncPreview);
    const [editableTargets, setEditableTargets] = useState({});

    useEffect(() => {
        if (active) {
            setEditableTargets(
                normalizeExpectedTargets(campaign?.expected_stats_targets)
            );
        }
    }, [active, campaign]);

    const screenRows = useMemo(() => {
        return buildScreenRows(
            campaign,
            syncPreview,
            editableTargets,
            screenStats
        );
    }, [campaign, editableTargets, screenStats, syncPreview]);

    const configuredScreensCount = screenRows.filter((screenRow) =>
        hasAnyConfiguredValue(screenRow.targetTotals)
    ).length;
    const busy = isSaving || isApplying;
    const hadConfiguredTargetsOnLoad = Object.values(
        normalizeExpectedTargets(campaign?.expected_stats_targets)
    ).some((targetTotals) => hasAnyConfiguredValue(targetTotals));
    const hasAtLeastOneEditableTarget =
        configuredScreensCount > 0 &&
        screenRows.some((screenRow) => hasAnyConfiguredValue(screenRow.targetTotals));

    const handleClose = () => {
        if (!busy) {
            toggler();
        }
    };

    const handleMetricChange = (screenId, metricKey, rawValue) => {
        setEditableTargets((currentTargets) => {
            const nextScreenTargets = {
                ...(currentTargets[screenId] || {}),
            };

            nextScreenTargets[metricKey] = normalizeMetricValue(rawValue);

            return {
                ...currentTargets,
                [screenId]: nextScreenTargets,
            };
        });
    };

    const title = hasPreview
        ? t(
              'sections.dashboard-individual.quick-controls.expected-stats.previewTitle'
          )
        : t(
              'sections.dashboard-individual.quick-controls.expected-stats.manageTitle'
          );
    const description = hasPreview
        ? t(
              'sections.dashboard-individual.quick-controls.expected-stats.previewDescription'
          )
        : t(
              'sections.dashboard-individual.quick-controls.expected-stats.manageDescription'
          );

    return (
        <Modal
            size="regular"
            active={active}
            toggler={handleClose}
            className={styles.modalShell}
            contentClassName={styles.modalPanel}>
            <div className={styles.header}>
                <ModalHeader
                    toggler={handleClose}
                    className={styles.modalHeader}
                    isHandle={!busy}>
                    <span className={styles.title}>
                        {title}
                    </span>
                </ModalHeader>
                <p className={styles.description}>{description}</p>
                {hasPreview && (
                    <div className={styles.summaryGrid}>
                        <div className={styles.summaryItem}>
                            <span>
                                {t(
                                    'sections.dashboard-individual.quick-controls.expected-stats.changedRows'
                                )}
                            </span>
                            <strong>{changedRows}</strong>
                        </div>
                        <div className={styles.summaryItem}>
                            <span>
                                {t(
                                    'sections.dashboard-individual.quick-controls.expected-stats.verifiedScreens'
                                )}
                            </span>
                            <strong>{verification.length}</strong>
                        </div>
                        <div className={styles.summaryItem}>
                            <span>
                                {t(
                                    'sections.dashboard-individual.quick-controls.expected-stats.missingScreens'
                                )}
                            </span>
                            <strong>{missingTargetScreens.length}</strong>
                        </div>
                    </div>
                )}
            </div>

            <ModalBody className={styles.body}>
                {screenRows.length === 0 ? (
                    <div className={styles.emptyState}>
                        <div>
                            <h4>
                                {t(
                                    'sections.dashboard-individual.quick-controls.expected-stats.emptyStateTitle'
                                )}
                            </h4>
                            <p>
                                {t(
                                    'sections.dashboard-individual.quick-controls.expected-stats.emptyStateDescription'
                                )}
                            </p>
                        </div>
                    </div>
                ) : (
                    <div className={styles.bodyStack}>
                        {!hasPreview && (
                            <div className={styles.notice}>
                                <div className={styles.noticeIcon}>
                                    <Icon
                                        name="lightbulb"
                                    />
                                </div>
                                <div>
                                    <strong>
                                        {t(
                                            'sections.dashboard-individual.quick-controls.expected-stats.saveHintTitle'
                                        )}
                                    </strong>
                                    <p>
                                        {t(
                                            'sections.dashboard-individual.quick-controls.expected-stats.saveHintDescription'
                                        )}
                                    </p>
                                </div>
                            </div>
                        )}

                        {hasPreview && verification.length === 0 && (
                            <div className={styles.warningNotice}>
                                {t(
                                    'sections.dashboard-individual.quick-controls.expected-stats.emptyPreview'
                                )}
                            </div>
                        )}

                        <div className={styles.rowsScroller}>
                            {screenRows.map((screenRow) => (
                                <section
                                    key={screenRow.screenId}
                                    className={styles.screenRow}>
                                    <div className={styles.screenMeta}>
                                        <div>
                                            <h4>{screenRow.screenName}</h4>
                                            <p>{screenRow.screenId}</p>
                                        </div>
                                        <span
                                            className={
                                                screenRow.hasStatsMatch
                                                    ? styles.statusOk
                                                    : styles.statusPending
                                            }>
                                            {screenRow.hasStatsMatch
                                                ? t(
                                                      'sections.dashboard-individual.quick-controls.expected-stats.statusMatched'
                                                  )
                                                : t(
                                                      'sections.dashboard-individual.quick-controls.expected-stats.statusPending'
                                                  )}
                                        </span>
                                        {hasPreview && (
                                            <div className={styles.rowsCount}>
                                                <span>
                                                    {t(
                                                        'sections.dashboard-individual.quick-controls.expected-stats.rows'
                                                    )}
                                                </span>
                                                <strong>{screenRow.rows}</strong>
                                            </div>
                                        )}
                                    </div>

                                    <div className={styles.metricGrid}>
                                        {METRICS.map((metric) => {
                                            const currentValue =
                                                screenRow.currentTotals?.[
                                                    metric.key
                                                ];
                                            const targetValue =
                                                screenRow.targetTotals?.[
                                                    metric.key
                                                ];

                                            return (
                                                <label
                                                    key={metric.key}
                                                    className={styles.metricCell}>
                                                    <span className={styles.metricLabel}>
                                                        {t(metric.labelKey)}
                                                    </span>
                                                    <span className={styles.currentValue}>
                                                        <small>
                                                            {t(
                                                                'sections.dashboard-individual.quick-controls.expected-stats.current'
                                                            )}
                                                        </small>
                                                        <strong>
                                                            {formatMetricValue(
                                                                metric.key,
                                                                currentValue,
                                                                currencySymbol
                                                            )}
                                                        </strong>
                                                    </span>
                                                    <span className={styles.inputWrap}>
                                                        {metric.key === 'spent' && (
                                                            <span className={styles.currencyPrefix}>
                                                                {currencySymbol}
                                                            </span>
                                                        )}
                                                        <input
                                                            type="number"
                                                            min="0"
                                                            step={metric.step}
                                                            value={targetValue ?? ''}
                                                            onChange={(event) =>
                                                                handleMetricChange(
                                                                    screenRow.screenId,
                                                                    metric.key,
                                                                    event.target.value
                                                                )
                                                            }
                                                            className={
                                                                metric.key ===
                                                                'spent'
                                                                    ? styles.currencyInput
                                                                    : ''
                                                            }
                                                            placeholder="0"
                                                        />
                                                    </span>
                                                    <span
                                                        className={`${styles.delta} ${getDeltaTone(
                                                            currentValue,
                                                            targetValue
                                                        )}`}>
                                                        {targetValue !== null &&
                                                        targetValue !== undefined
                                                            ? formatDeltaValue(
                                                                  metric.key,
                                                                  currentValue,
                                                                  targetValue,
                                                                  currencySymbol
                                                              )
                                                            : '-'}
                                                    </span>
                                                </label>
                                            );
                                        })}
                                    </div>
                                </section>
                            ))}
                        </div>
                    </div>
                )}
            </ModalBody>

            <ModalFooter className={styles.footer}>
                <div className={styles.footerCount}>
                    {configuredScreensCount}{' '}
                    {t(
                        'sections.dashboard-individual.quick-controls.expected-stats.configuredScreens'
                    )}
                </div>
                <div className={styles.actions}>
                    <CommonButton
                        label={t('common.buttons.cancel')}
                        styleType="secondary"
                        onClick={handleClose}
                        disabled={busy}
                        className={styles.secondaryButton}
                    />
                    <CommonButton
                        label={t(
                            'sections.dashboard-individual.quick-controls.expected-stats.save'
                        )}
                        styleType={hasPreview ? 'secondary' : 'primary'}
                        onClick={() => onSave(editableTargets)}
                        disabled={
                            busy ||
                            screenRows.length === 0 ||
                            (!hasAtLeastOneEditableTarget &&
                                !hadConfiguredTargetsOnLoad)
                        }
                        className={
                            hasPreview
                                ? styles.secondaryButton
                                : styles.primaryButton
                        }
                        useLoader
                    />
                    {hasPreview && (
                        <CommonButton
                            label={t(
                                'sections.dashboard-individual.quick-controls.expected-stats.apply'
                            )}
                            styleType="primary"
                            onClick={() => onConfirmSync(editableTargets)}
                            disabled={!hasAtLeastOneEditableTarget || busy}
                            className={styles.primaryButton}
                            useLoader
                        />
                    )}
                </div>
            </ModalFooter>
        </Modal>
    );
};

export default ExpectedStatsSyncModal;
